import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import type { SystemOverview } from "@trading-os/shared-types";
import { api } from "./api";

const pages = [
  "Overview", "Live", "Trades", "Sessions", "Analytics", "Backtests",
  "Strategy Lab", "Knowledge", "Risk", "Account", "System Health", "Settings"
] as const;
type Page = typeof pages[number];

function formatCurrency(value: number | null): string {
  if (value === null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function formatTime(value: string | null): string {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "America/Vancouver"
  }).format(new Date(value));
}

function Metric({ label, value, hint, tone = "default" }: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  return (
    <article className={`metric metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint && <small>{hint}</small>}
    </article>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty-state">
      <span className="empty-state__line" />
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(password);
      onAuthenticated();
    } catch {
      setError("Access denied. Check the Trading OS admin password.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="wordmark"><span>TRADING</span> OS</div>
        <div className="paper-pill">PAPER INFRASTRUCTURE</div>
        <h1>Mission control for one mechanical trading system.</h1>
        <p>Execution stays deterministic. Research proposes evidence. Nothing promotes itself.</p>
        <form onSubmit={submit}>
          <label htmlFor="password">Admin password</label>
          <input id="password" type="password" autoComplete="current-password" minLength={12}
            value={password} onChange={(event) => setPassword(event.target.value)} required />
          {error && <div className="form-error" role="alert">{error}</div>}
          <button disabled={busy}>{busy ? "Verifying…" : "Open Trading OS"}</button>
        </form>
        <small>V1 is hard-locked to MNQ · Sim101 · PAPER.</small>
      </section>
    </main>
  );
}

function OverviewPage({ data }: { data: SystemOverview }) {
  const winRate = data.tradesToday === 0 ? "—" : `${Math.round((data.wins / data.tradesToday) * 100)}%`;
  return (
    <>
      <section className="hero-status">
        <div>
          <span className="eyebrow">Execution posture</span>
          <h2>{data.tradingEnabled ? "Trading enabled" : "Trading disabled"}</h2>
          <p>{data.executionHeartbeatAt
            ? `Last NinjaTrader heartbeat ${formatTime(data.executionHeartbeatAt)}`
            : "NinjaTrader has not connected. Local risk remains authoritative."}</p>
        </div>
        <div className={`risk-orb risk-orb--${data.riskState.toLowerCase()}`}>
          <span>{data.riskState}</span>
          <small>RISK STATE</small>
        </div>
      </section>
      <section className="metric-grid metric-grid--primary">
        <Metric label="TODAY'S P&L" value={formatCurrency(data.todayPnlDollars)} tone={data.todayPnlDollars > 0 ? "good" : data.todayPnlDollars < 0 ? "bad" : "default"} />
        <Metric label="TODAY'S R" value={`${data.todayR.toFixed(2)}R`} />
        <Metric label="POSITION" value={data.currentPosition} hint={data.currentPrice ? `MNQ ${data.currentPrice}` : "No verified market price"} />
        <Metric label="DRAWDOWN LEFT" value={formatCurrency(data.drawdownRemaining)} hint={data.drawdownUsed === null ? "Awaiting account snapshot" : `${formatCurrency(data.drawdownUsed)} used`} />
      </section>
      <section className="split-grid">
        <div className="panel">
          <div className="panel__head"><div><span className="eyebrow">Session telemetry</span><h3>New York ORB</h3></div><span className="status-dot status-dot--idle">CLOSED</span></div>
          <div className="orb-visual">
            <div><span>ORB HIGH</span><strong>{data.orbHigh ?? "—"}</strong></div>
            <div className="orb-range"><i /><span>15 MINUTE RANGE</span><i /></div>
            <div><span>ORB LOW</span><strong>{data.orbLow ?? "—"}</strong></div>
          </div>
          <div className="mini-grid">
            <Metric label="TRADES" value={data.tradesToday} />
            <Metric label="W / L" value={`${data.wins} / ${data.losses}`} />
            <Metric label="WIN RATE" value={winRate} />
          </div>
        </div>
        <div className="panel">
          <div className="panel__head"><div><span className="eyebrow">Control plane</span><h3>System truth</h3></div></div>
          <dl className="truth-list">
            <div><dt>API</dt><dd className="good">ONLINE</dd></div>
            <div><dt>Database</dt><dd className="good">CONNECTED</dd></div>
            <div><dt>Execution</dt><dd className={data.executionHeartbeatAt ? "good" : "warn"}>{data.executionHeartbeatAt ? "CONNECTED" : "OFFLINE"}</dd></div>
            <div><dt>Market data</dt><dd className={data.marketDataAt ? "good" : "warn"}>{data.marketDataAt ? formatTime(data.marketDataAt) : "NO FEED"}</dd></div>
            <div><dt>Strategy</dt><dd>{data.activeStrategyVersion}</dd></div>
            <div><dt>Account</dt><dd>Sim101 ONLY</dd></div>
          </dl>
        </div>
      </section>
    </>
  );
}

function TablePage({ title, description, rows, columns, empty }: {
  title: string;
  description: string;
  rows: Array<Record<string, unknown>>;
  columns: Array<{ key: string; label: string }>;
  empty: string;
}) {
  return (
    <section className="panel page-panel">
      <div className="page-title"><div><span className="eyebrow">Evidence ledger</span><h2>{title}</h2><p>{description}</p></div><span>{rows.length} records</span></div>
      {rows.length === 0 ? <EmptyState title={`No ${title.toLowerCase()} yet`} body={empty} /> : (
        <div className="table-wrap"><table><thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
          <tbody>{rows.map((row, index) => <tr key={String(row.id ?? index)}>{columns.map((column) => <td key={column.key}>{String(row[column.key] ?? "—")}</td>)}</tr>)}</tbody></table></div>
      )}
    </section>
  );
}

export function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [page, setPage] = useState<Page>("Overview");
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [trades, setTrades] = useState<Array<Record<string, unknown>>>([]);
  const [rejected, setRejected] = useState<Array<Record<string, unknown>>>([]);
  const [strategies, setStrategies] = useState<Array<Record<string, unknown>>>([]);
  const [health, setHealth] = useState<{ services: Array<Record<string, unknown>>; incidents: Array<Record<string, unknown>> } | null>(null);
  const [error, setError] = useState("");
  const [navOpen, setNavOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [overviewData, tradeData, rejectedData, strategyData, healthData] = await Promise.all([
        api.overview(), api.trades(), api.rejected(), api.strategies(), api.health()
      ]);
      setOverview(overviewData);
      setTrades(tradeData.trades);
      setRejected(rejectedData.rejectedSignals);
      setStrategies(strategyData.strategyVersions);
      setHealth(healthData);
      setError("");
    } catch (cause) {
      if ((cause as { status?: number }).status === 401) setAuthenticated(false);
      else setError("Live system data is temporarily unavailable.");
    }
  }, []);

  useEffect(() => {
    api.session().then(() => {
      setAuthenticated(true);
      return refresh();
    }).catch(() => setAuthenticated(false));
  }, [refresh]);

  useEffect(() => {
    if (!authenticated) return;
    const timer = window.setInterval(refresh, 15_000);
    return () => window.clearInterval(timer);
  }, [authenticated, refresh]);

  const content = useMemo(() => {
    if (!overview) return <div className="loading">Loading verified system state…</div>;
    if (page === "Overview" || page === "Live") return <OverviewPage data={overview} />;
    if (page === "Trades") return <TablePage title="Trades" description="Executed Sim101 records with deterministic entry evidence."
      rows={trades} columns={[{ key: "opened_at", label: "OPENED" }, { key: "direction", label: "SIDE" }, { key: "entry", label: "ENTRY" }, { key: "exit", label: "EXIT" }, { key: "net_pnl", label: "P&L" }, { key: "r_multiple", label: "R" }, { key: "strategy_version", label: "VERSION" }]}
      empty="Trades appear only after authenticated NinjaTrader events. Trading OS does not create sample fills." />;
    if (page === "Knowledge") return <TablePage title="Rejected setups" description="Setups denied by deterministic risk remain available for hypothetical-outcome research."
      rows={rejected} columns={[{ key: "rejected_at", label: "TIME" }, { key: "direction", label: "SIDE" }, { key: "reason_code", label: "REASON" }, { key: "entry", label: "ENTRY" }, { key: "strategy_version", label: "VERSION" }]}
      empty="Rejected signals will be retained once the execution machine connects." />;
    if (page === "Strategy Lab") return <TablePage title="Strategy versions" description="Definitions are immutable. Rule changes create a new version and require approval."
      rows={strategies} columns={[{ key: "strategy_id", label: "STRATEGY" }, { key: "version", label: "VERSION" }, { key: "session_name", label: "SESSION" }, { key: "opening_range_minutes", label: "ORB" }, { key: "approval_status", label: "APPROVAL" }, { key: "enabled", label: "ENABLED" }]}
      empty="No strategy versions are registered." />;
    if (page === "System Health") return <TablePage title="System health" description="Heartbeats and incidents across the execution and research boundary."
      rows={health?.services ?? []} columns={[{ key: "service", label: "SERVICE" }, { key: "status", label: "STATUS" }, { key: "observed_at", label: "LAST SEEN" }, { key: "version", label: "VERSION" }]}
      empty="The API and database are online. External workers have not emitted a heartbeat yet." />;
    const messages: Record<string, [string, string]> = {
      Sessions: ["Session archive", "Asia and London are configured but disabled. New York becomes populated from verified bar data."],
      Analytics: ["Analytics", "Equity, expectancy, drawdown, direction, weekday, and filter comparisons appear after measured samples exist."],
      Backtests: ["Backtest registry", "Fixture tests are available. Historical runs appear only when a fingerprinted market dataset is supplied."],
      Risk: ["Local risk authority", "V1 is hard-locked to Sim101. Local NinjaTrader risk operates independently of this dashboard."],
      Account: ["Account", "No account snapshot has been received. Brokerage credentials are never exposed to the browser."],
      Settings: ["Settings", "Runtime settings are read-only here. Strategy and risk changes require a new version and explicit approval."]
    };
    const [title, body] = messages[page] ?? [page, "No verified data is available."];
    return <section className="panel page-panel"><div className="page-title"><div><span className="eyebrow">Controlled surface</span><h2>{title}</h2></div></div><EmptyState title="Awaiting verified inputs" body={body} /></section>;
  }, [health, overview, page, rejected, strategies, trades]);

  if (authenticated === null) return <div className="boot-screen"><div className="wordmark"><span>TRADING</span> OS</div></div>;
  if (!authenticated) return <Login onAuthenticated={() => { setAuthenticated(true); void refresh(); }} />;

  return (
    <div className="app-shell">
      <aside className={navOpen ? "sidebar sidebar--open" : "sidebar"}>
        <div className="sidebar__top"><div className="wordmark"><span>TRADING</span> OS</div><button className="close-nav" onClick={() => setNavOpen(false)}>×</button></div>
        <div className="mode-card"><div><span className="pulse" /><strong>PAPER</strong></div><small>MNQ · Sim101</small></div>
        <nav>{pages.map((item) => <button key={item} className={page === item ? "active" : ""} onClick={() => { setPage(item); setNavOpen(false); }}><span>{item.slice(0, 2).toUpperCase()}</span>{item}</button>)}</nav>
        <div className="sidebar__foot"><span>Strategy</span><strong>{overview?.activeStrategyVersion ?? "—"}</strong><button onClick={async () => { await api.logout(); setAuthenticated(false); }}>Lock dashboard</button></div>
      </aside>
      <main className="workspace">
        <header><button className="menu" onClick={() => setNavOpen(true)}>☰</button><div><span className="eyebrow">TRADING OS / {page.toUpperCase()}</span><h1>{page}</h1></div><div className="header-status"><span className={overview?.executionHeartbeatAt ? "status-dot status-dot--online" : "status-dot status-dot--idle"}>{overview?.executionHeartbeatAt ? "SYSTEM ONLINE" : "EXECUTION OFFLINE"}</span><button onClick={refresh}>Refresh</button></div></header>
        {error && <div className="banner banner--error" role="alert">{error}</div>}
        <div className="content">{content}</div>
      </main>
    </div>
  );
}
